/* eslint-disable complexity */
import React, { useState } from "react";
import { Box, LinearProgress } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { FormInputTel } from "@imminently/react/ui/forms";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";

import ExistingDelegateSearchOverlay from "./ExistingDelegateSearchOverlay";
import { Patient } from "@/resources/types";

interface DelegatePhoneFieldProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  sx?: any;
}

// Global state to manage single overlay instance
const globalOverlayState = {
  isOpen: false,
  setOpen: (open: boolean) => {
    globalOverlayState.isOpen = open;
  },
  closeOverlay: () => {
    globalOverlayState.isOpen = false;
  }
};

// Validate phone number format using normalizePhoneNumber logic
// If normalizePhoneNumber would succeed, the phone is valid
const isValidPhoneFormat = (phoneValue: string | undefined): boolean => {
  if (!phoneValue || typeof phoneValue !== "string") {
    return false;
  }
  const phoneTrimmed = phoneValue.trim();
  if (phoneTrimmed === "") {
    return false;
  }

  try {
    // Try to parse the phone number - if it throws or returns null, it's invalid
    // Use parsePhoneNumberFromString which matches normalizePhoneNumber behavior
    const phoneNumber = parsePhoneNumberFromString(phoneTrimmed);
    // If parsing succeeds and is valid, it's a valid phone number
    return phoneNumber !== undefined && phoneNumber !== null && isValidPhoneNumber(phoneNumber.number);
  } catch (e) {
    // normalizePhoneNumber would throw, so this is invalid
    return false;
  }
};

const DelegatePhoneField: React.FC<DelegatePhoneFieldProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  sx = {},
}) => {
  const { control, setValue, trigger } = useFormContext();
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const phone = useWatch({ control, name });
  const hasDelegatePresent = useWatch({ control, name: "hasDelegatePresent" });
  const delegateId = useWatch({ control, name: "delegateId" });
  const isEditMode = useWatch({ control, name: "_isEditMode" });

  // Check if this field should show the overlay
  // Don't show in edit mode, when disabled, or if delegate is already selected
  const phoneIsValid = isValidPhoneFormat(phone);
  const shouldShowOverlay = globalOverlayState.isOpen && !disabled && !isEditMode && hasDelegatePresent && !delegateId && phoneIsValid;

  const handleLoadingChange = React.useCallback((isLoading: boolean) => {
    setIsSearchLoading(current => current === isLoading ? current : isLoading);
  }, []);

  const closeDelegateSearchOverlay = React.useCallback(() => {
    setIsSearchLoading(current => current === false ? current : false);
    globalOverlayState.closeOverlay();
  }, []);

  const handleSelectDelegate = async (person: Patient) => {
    closeDelegateSearchOverlay();

    // Populate form with existing person data
    setValue("delegateFirstName", person.firstName || "");
    setValue("delegateLastName", person.lastName || "");
    setValue("delegateEmail", person.email || "");

    // Set delegate ID for backend linking
    // Prefer identity_id for strong delegation, fallback to id (UUID)
    const personAny = person as any;
    if (personAny.identity_id) {
      setValue("delegateId", personAny.identity_id);
    } else if (person.id) {
      setValue("delegateId", person.id);
    }

    await trigger();
  };

  const handleCloseOverlay = () => {
    closeDelegateSearchOverlay();
  };

  const handleFocus = () => {
    if (disabled || isEditMode || !hasDelegatePresent || delegateId) return;
    // Set overlay open when focusing - overlay will show automatically if conditions are met
    globalOverlayState.setOpen(true);
  };

  const handleKeyUp = () => {
    if (disabled || isEditMode || !hasDelegatePresent || delegateId) return;
    // Set overlay open on keystroke - overlay will show automatically if conditions are met
    globalOverlayState.setOpen(true);
  };

  // If field becomes disabled, delegate is selected, or in edit mode, ensure any loading/overlay is cleared
  React.useEffect(() => {
    if (disabled || isEditMode || !hasDelegatePresent || delegateId) {
      closeDelegateSearchOverlay();
    }
  }, [disabled, isEditMode, hasDelegatePresent, delegateId, closeDelegateSearchOverlay]);

  // Auto-trigger overlay when phone becomes valid
  React.useEffect(() => {
    if (disabled || isEditMode || !hasDelegatePresent || delegateId) {
      closeDelegateSearchOverlay();
      return;
    }

    if (isValidPhoneFormat(phone)) {
      globalOverlayState.setOpen(true);
    } else {
      closeDelegateSearchOverlay();
    }
  }, [phone, disabled, isEditMode, hasDelegatePresent, delegateId, closeDelegateSearchOverlay]);

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <FormInputTel
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        control={control}
        sx={{ width: "100%" }}
        onFocus={handleFocus}
        onKeyUp={handleKeyUp}
        autoComplete="off"
        slotProps={{ htmlInput: { autoComplete: "off" } }}
      />

      {/* Show loading indicator below the field when searching */}
      {isSearchLoading && !disabled && !isEditMode && hasDelegatePresent && !delegateId && (
        <LinearProgress
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            borderRadius: "0 0 4px 4px"
          }}
        />
      )}

      {shouldShowOverlay && phone && (
        <ExistingDelegateSearchOverlay
          phone={phone}
          onSelectDelegate={handleSelectDelegate}
          onClose={handleCloseOverlay}
          onLoadingChange={handleLoadingChange}
        />
      )}
    </Box>
  );
};

export default DelegatePhoneField;
